import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label as ShadcnLabel } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface SelectOption {
    value: string;
    label: string;
    disabled?: boolean;
}

interface SelectFieldProps {
    label?: string;
    placeholder?: string;
    value?: string;
    defaultValue?: string;
    onValueChange?: (value: string) => void;
    options: SelectOption[] | string[];
    disabled?: boolean;
    error?: string;
    description?: string;
    className?: string;
    id?: string;
}

export const SelectField = React.forwardRef<HTMLButtonElement, SelectFieldProps>(
    (
        {
            className,
            label,
            placeholder = 'Select an option...',
            value,
            defaultValue,
            onValueChange,
            options,
            disabled,
            error,
            description,
            id,
            ...props
        },
        ref,
    ) => {
        // Generate a unique ID if not provided and we have a label
        const selectId = id || (label ? `select-${Math.random().toString(36).substr(2, 9)}` : undefined);

        // Normalize options to always have value/label structure
        const normalizedOptions = React.useMemo(() => {
            return options.map(option => {
                if (typeof option === 'string') {
                    return { value: option, label: option };
                }
                return option;
            });
        }, [options]);

        const selectComponent = (
            <Select value={value} defaultValue={defaultValue} onValueChange={onValueChange} disabled={disabled}>
                <SelectTrigger
                    ref={ref}
                    id={selectId}
                    className={cn(
                        // Glue's select styling
                        'h-8 rounded border border-border bg-background text-foreground',
                        'focus:ring-1 focus:ring-ring focus:border-ring',
                        error && 'border-destructive focus:border-destructive focus:ring-destructive',
                        className,
                    )}
                    {...props}
                >
                    <SelectValue placeholder={placeholder} />
                </SelectTrigger>
                <SelectContent>
                    {normalizedOptions.map(option => (
                        <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
                            {option.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        );

        if (label) {
            return (
                <div className="flex flex-col space-y-2">
                    <ShadcnLabel htmlFor={selectId} className={cn(error && 'text-destructive')}>
                        {label}
                    </ShadcnLabel>
                    {selectComponent}
                    {error && <p className="text-sm text-destructive">{error}</p>}
                    {description && !error && <p className="text-sm text-muted-foreground">{description}</p>}
                </div>
            );
        }

        return selectComponent;
    },
);

SelectField.displayName = 'SelectField';
